import { useState, useRef, useCallback } from 'react';

export function useWebRecorder(disableVAD = false) {
  const [isRecording, setIsRecording] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [voiceActivity, setVoiceActivity] = useState(0);
  const [isSilent, setIsSilent] = useState(false);
  const [recordingDuration, setRecordingDuration] = useState(0); // Duration in milliseconds

  const recordingStartTimeRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const mediaChunksRef = useRef([]);
  const streamRef = useRef(null);

  // Voice activity meter refs
  const analyserRef = useRef(null);
  const rafRef = useRef(null);
  const pausedTimeRef = useRef(0);

  // Auto-pause/resume refs
  const silenceStartTimeRef = useRef(null);
  const autoPauseDelayMs = 2000;

  // Duration tracking
  const durationIntervalRef = useRef(null);

  const startWebRecording = useCallback(async () => {
    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      alert('Audio recording not supported by this browser.');
      return false;
    }

    try {
      const constraints = {
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
          sampleRate: 16000
        }
      };

      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      streamRef.current = stream;

      const options = {
        mimeType: 'audio/webm;codecs=opus',
        audioBitsPerSecond: 128000
      };

      // Setup voice activity metering
      try {
        const AudioCtx = window.AudioContext || window.webkitAudioContext;
        const ctx = new AudioCtx();
        const srcNode = ctx.createMediaStreamSource(stream);
        const analyser = ctx.createAnalyser();
        analyser.fftSize = 256;
        srcNode.connect(analyser);
        analyserRef.current = { analyser, ctx };

        const buf = new Uint8Array(analyser.frequencyBinCount);
        const updateMeter = () => {
          analyser.getByteTimeDomainData(buf);
          let sum = 0;
          for (let i = 0; i < buf.length; i++) {
            const val = (buf[i] - 128) / 128;
            sum += val * val;
          }
          const rms = Math.sqrt(sum / buf.length);
          const currentlySilent = rms < 0.07; // Increased threshold: louder sounds now considered silent

          setVoiceActivity(rms * 100); // Convert to 0-100 scale
          setIsSilent(currentlySilent);

          // Auto-pause/resume logic (skip if VAD disabled)
          if (!disableVAD) {
            const mediaRecorder = mediaRecorderRef.current;
            const now = Date.now();

            if (currentlySilent) {
              // Start tracking silence
              if (silenceStartTimeRef.current === null) {
                silenceStartTimeRef.current = now;
              }

              // Check if silence duration exceeds threshold
              const silenceDuration = now - silenceStartTimeRef.current;
              if (silenceDuration >= autoPauseDelayMs && mediaRecorder?.state === 'recording') {
                mediaRecorder.pause();
                setIsPaused(true);
              }
            } else {
              // Voice detected
              if (silenceStartTimeRef.current !== null) {
                silenceStartTimeRef.current = null;
              }

              // Auto-resume if currently paused
              if (mediaRecorder?.state === 'paused') {
                mediaRecorder.resume();
                setIsPaused(false);
              }
            }
          }

          rafRef.current = requestAnimationFrame(updateMeter);
        };
        rafRef.current = requestAnimationFrame(updateMeter);
      } catch (meterErr) {
        // Log removed: console.warn('Voice meter setup failed:', meterErr);
      }

      const mediaRecorder = new MediaRecorder(stream, options);
      mediaRecorderRef.current = mediaRecorder;
      mediaChunksRef.current = [];

      mediaRecorder.ondataavailable = (event) => {
        if (event.data && event.data.size > 0) {
          mediaChunksRef.current.push(event.data);
        }
      };

      mediaRecorder.start(1000);
      return true;
    } catch (error) {
      // Log removed: console.error('❌ Web recording error:', error);
      return false;
    }
  }, []);

  const stopWebRecording = useCallback(async () => {
    const mediaRecorder = mediaRecorderRef.current;
    if (!mediaRecorder || mediaRecorder.state === 'inactive') {
      return new Blob([], { type: 'audio/webm' });
    }

    return new Promise((resolve) => {
      mediaRecorder.onstop = () => {
        const mimeType = mediaRecorder.mimeType || 'audio/webm';
        const audioBlob = new Blob(mediaChunksRef.current, { type: mimeType });

        if (streamRef.current) {
          streamRef.current.getTracks().forEach(track => track.stop());
          streamRef.current = null;
        }

        // Cleanup meter
        if (rafRef.current) {
          cancelAnimationFrame(rafRef.current);
          rafRef.current = null;
        }
        if (analyserRef.current) {
          analyserRef.current.ctx.close();
          analyserRef.current = null;
        }

        mediaRecorderRef.current = null;
        mediaChunksRef.current = [];

        resolve(audioBlob);
      };

      mediaRecorder.stop();
    });
  }, []);

  const startRecording = useCallback(async () => {
    recordingStartTimeRef.current = Date.now();
    pausedTimeRef.current = 0;
    setIsRecording(true);
    setRecordingDuration(0);

    const success = await startWebRecording();

    if (!success) {
      setIsRecording(false);
      recordingStartTimeRef.current = null;
    } else {
      if (!disableVAD) {
        // Start in paused state - will auto-resume when voice is detected
        setIsPaused(true);

        // Immediately pause the MediaRecorder
        const mediaRecorder = mediaRecorderRef.current;
        if (mediaRecorder && mediaRecorder.state === 'recording') {
          mediaRecorder.pause();
        }
      } else {
        // VAD disabled - start recording immediately
        setIsPaused(false);
      }

      // Start duration tracking
      durationIntervalRef.current = setInterval(() => {
        if (recordingStartTimeRef.current) {
          const elapsed = Date.now() - recordingStartTimeRef.current - pausedTimeRef.current;
          setRecordingDuration(elapsed);
        }
      }, 100); // Update every 100ms
    }

    return success;
  }, [startWebRecording]);

  const stopRecording = useCallback(async () => {
    // Check minimum recording duration
    const startTime = recordingStartTimeRef.current;
    if (startTime) {
      const duration = Date.now() - startTime - pausedTimeRef.current;
      const minDuration = 1000;
      if (duration < minDuration) {
        await new Promise(resolve => setTimeout(resolve, minDuration - duration));
      }
    }

    // Stop duration tracking
    if (durationIntervalRef.current) {
      clearInterval(durationIntervalRef.current);
      durationIntervalRef.current = null;
    }

    setIsRecording(false);
    setIsPaused(false);
    setVoiceActivity(0);
    setIsSilent(false);

    let audioBlob;
    try {
      audioBlob = await stopWebRecording();
    } catch (error) {
      // Log removed: console.error('❌ Error stopping recording:', error);
      audioBlob = new Blob([], { type: 'audio/webm' });
    }

    const finalDuration = recordingDuration;
    recordingStartTimeRef.current = null;
    pausedTimeRef.current = 0;
    setRecordingDuration(0);

    return audioBlob;
  }, [stopWebRecording, recordingDuration]);

  const pauseRecording = useCallback(async () => {
    const mediaRecorder = mediaRecorderRef.current;
    if (mediaRecorder && mediaRecorder.state === 'recording') {
      mediaRecorder.pause();
      setIsPaused(true);
      setIsRecording(false); // Update state so UI shows play button
      if (recordingStartTimeRef.current) {
        pausedTimeRef.current = Date.now() - recordingStartTimeRef.current;
      }
    }
  }, []);

  const resumeRecording = useCallback(async () => {
    const mediaRecorder = mediaRecorderRef.current;
    if (mediaRecorder && mediaRecorder.state === 'paused') {
      mediaRecorder.resume();
      setIsPaused(false);
      setIsRecording(true); // Update state so UI shows mic visualizer
      if (recordingStartTimeRef.current && pausedTimeRef.current) {
        recordingStartTimeRef.current = Date.now() - pausedTimeRef.current;
      }
    }
  }, []);

  const resetRecorder = useCallback(async () => {
    setIsRecording(false);
    setIsPaused(false);
    setVoiceActivity(0);
    setIsSilent(false);
    setRecordingDuration(0);
    recordingStartTimeRef.current = null;
    pausedTimeRef.current = 0;
    silenceStartTimeRef.current = null; // Reset silence tracker

    // Stop duration tracking
    if (durationIntervalRef.current) {
      clearInterval(durationIntervalRef.current);
      durationIntervalRef.current = null;
    }

    // Clean up web resources
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
    if (mediaRecorderRef.current) {
      mediaRecorderRef.current = null;
    }
    mediaChunksRef.current = [];

    // Cleanup meter
    if (rafRef.current) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }
    if (analyserRef.current) {
      analyserRef.current.ctx.close();
      analyserRef.current = null;
    }
  }, []);

  // Helper method to get current duration
  const getRecordingDuration = useCallback(() => {
    if (!recordingStartTimeRef.current) return 0;
    return Date.now() - recordingStartTimeRef.current - pausedTimeRef.current;
  }, []);

  return {
    // State
    isRecording,
    isPaused,
    voiceActivity,
    isSilent,
    recordingDuration, // Duration in milliseconds
    mediaStream: streamRef.current,

    // Methods
    startRecording,
    stopRecording,
    pauseRecording,
    resumeRecording,
    resetRecorder,
    getRecordingDuration // Helper to get current duration
  };
}
